import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileArchiveIcon, FilmIcon, Loader2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useImagesOfSamples } from '@/services/image.service';
import {
    buildExportDownloadUrl,
    triggerBrowserDownload,
    usePrepareExport,
    type ExportType,
} from '@/services/export.service';
import { SampleModel } from '@/types/models';
import Section from '../../components/Section';

const PERIODS = [
    { value: '0.1', label: '0.1 s / image' },
    { value: '0.2', label: '0.2 s / image' },
    { value: '0.4', label: '0.4 s / image' },
    { value: '0.6', label: '0.6 s / image' },
];

const pad = (n: number) => String(n).padStart(2, '0');
// Date -> value for <input type="datetime-local"> in local wall-clock time.
const toLocalInput = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
        d.getHours(),
    )}:${pad(d.getMinutes())}`;

/** An export over one or more samples: a range of their acquisitions as one
 *  zip of jp2s, a folder per sample, or as a timelapse per sample. */
export default function ExportForm({
    projectId,
    samples,
}: {
    projectId: string;
    samples: SampleModel[];
}) {
    const { t } = useTranslation();
    const { mutateAsync: prepare, isPending } = usePrepareExport();
    const [pendingType, setPendingType] = useState<ExportType | null>(null);

    // A lone sample is the only choice there is.
    const [selected, setSelected] = useState<Set<string>>(
        () => new Set(samples.length === 1 ? [samples[0].id] : []),
    );
    // In the list's order, whatever order they were ticked in.
    const selectedIds = useMemo(
        () => samples.filter((s) => selected.has(s.id)).map((s) => s.id),
        [samples, selected],
    );
    const toggle = (id: string, on: boolean) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (on) next.add(id);
            else next.delete(id);
            return next;
        });

    const imageQueries = useImagesOfSamples(
        projectId,
        samples.map((s) => s.id),
    );
    const imagesBySample = useMemo(
        () =>
            new Map(samples.map((s, i) => [s.id, imageQueries[i]?.data ?? []])),
        [samples, imageQueries],
    );
    const isLoading = selectedIds.some(
        (id) => imageQueries[samples.findIndex((s) => s.id === id)]?.isLoading,
    );

    // The span of the selected samples' acquisitions, all together.
    const bounds = useMemo(() => {
        const times = selectedIds.flatMap((id) =>
            (imagesBySample.get(id) ?? []).map((i) =>
                new Date(i.acquisitionDate).getTime(),
            ),
        );
        if (times.length === 0) return null;
        return {
            min: new Date(Math.min(...times)),
            max: new Date(Math.max(...times)),
        };
    }, [selectedIds, imagesBySample]);

    const [name, setName] = useState('');
    const [fromLocal, setFromLocal] = useState('');
    const [toLocal, setToLocal] = useState('');
    const [period, setPeriod] = useState('0.2');
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    // The range starts as the full span, and starts again whenever the
    // selection changes, since the span does.
    const [rangeFor, setRangeFor] = useState<string | null>(null);
    const selectionKey = selectedIds.join(',');
    useEffect(() => {
        if (bounds && !isLoading && rangeFor !== selectionKey) {
            setFromLocal(toLocalInput(bounds.min));
            setToLocal(toLocalInput(bounds.max));
            setRangeFor(selectionKey);
        }
    }, [bounds, isLoading, rangeFor, selectionKey]);

    // Inclusive [from, to] at minute granularity (acquisitions are hours apart).
    const range = useMemo(() => {
        if (!fromLocal || !toLocal) return null;
        const from = new Date(fromLocal);
        const to = new Date(toLocal);
        to.setSeconds(59, 999);
        if (isNaN(from.getTime()) || isNaN(to.getTime()) || from > to)
            return null;
        return { from, to };
    }, [fromLocal, toLocal]);

    const countInRange = (id: string) => {
        if (!range) return 0;
        return (imagesBySample.get(id) ?? []).filter((i) => {
            const d = new Date(i.acquisitionDate).getTime();
            return d >= range.from.getTime() && d <= range.to.getTime();
        }).length;
    };
    const counts = new Map(selectedIds.map((id) => [id, countInRange(id)]));
    const selectedCount = [...counts.values()].reduce((a, b) => a + b, 0);
    const samplesWithImages = selectedIds.filter((id) => counts.get(id)! > 0);

    const handleExport = async (type: ExportType) => {
        setError(null);
        setNotice(null);
        if (!range) return;
        if (!name.trim()) {
            setError(t('Please enter an extraction name.'));
            return;
        }
        if (selectedCount === 0) {
            setError(t('No images in the selected range.'));
            return;
        }
        setPendingType(type);
        try {
            const { tokens } = await prepare({
                projectId,
                sampleIds: samplesWithImages,
                type,
                from: range.from.toISOString(),
                to: range.to.toISOString(),
                name: name.trim(),
                period: type === 'timelapse' ? period : undefined,
            });
            // Tokens authorize the downloads; trigger them without leaving
            // the page.
            for (const token of tokens)
                triggerBrowserDownload(
                    buildExportDownloadUrl(projectId, token),
                );
            setNotice(
                type === 'timelapse'
                    ? tokens.length > 1
                        ? t(
                              'Generating {{count}} timelapses, one after another — each download will start when it is ready.',
                              { count: tokens.length },
                          )
                        : t(
                              'Generating your timelapse — the download will start automatically.',
                          )
                    : t('Your download will start automatically.'),
            );
        } catch {
            setError(t('Export failed. Please try again.'));
        } finally {
            setPendingType(null);
        }
    };

    const allSelected = selected.size === samples.length;
    const ready = !isPending && selectedCount > 0;

    // One block, laid out like the project's settings: labels over
    // full-width fields, then, under a rule, each format a row with its
    // action on the right.
    return (
        <Section>
            <div className="space-y-5">
                <div>
                    <Label htmlFor="extraction-name">
                        {t('Extraction name')}
                    </Label>
                    <Input
                        id="extraction-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('e.g. week12 or full-season')}
                        className="mt-1.5"
                    />
                </div>

                <div>
                    <div className="flex items-center justify-between">
                        <Label>{t('Samples')}</Label>
                        {samples.length > 1 && (
                            <button
                                type="button"
                                onClick={() =>
                                    setSelected(
                                        new Set(
                                            allSelected
                                                ? []
                                                : samples.map((s) => s.id),
                                        ),
                                    )
                                }
                                className="text-[13px] text-[#2d6a4f] hover:underline"
                            >
                                {allSelected ? t('Clear') : t('Select all')}
                            </button>
                        )}
                    </div>
                    <div className="mt-1.5 max-h-64 overflow-y-auto rounded-md border border-[#dadbd5] divide-y divide-[#ecede8]">
                        {samples.map((s) => (
                            <label
                                key={s.id}
                                className="flex h-10 cursor-pointer items-center gap-3 px-3 text-sm text-[#1c2620] hover:bg-[#f7f8f5]"
                            >
                                <Checkbox
                                    checked={selected.has(s.id)}
                                    onCheckedChange={(on) =>
                                        toggle(s.id, on === true)
                                    }
                                />
                                <span className="flex-1 min-w-0 truncate">
                                    {s.name}
                                </span>
                                {selected.has(s.id) && range && (
                                    <span className="text-xs text-[#7a8077] tabular-nums">
                                        {t('{{count}} image(s)', {
                                            count: counts.get(s.id),
                                        })}
                                    </span>
                                )}
                            </label>
                        ))}
                    </div>
                </div>

                {selectedIds.length === 0 ? (
                    <p className="text-sm text-[#7a8077]">
                        {t('Select at least one sample.')}
                    </p>
                ) : isLoading ? (
                    <Skeleton className="w-full h-[68px] rounded-md" />
                ) : !bounds ? (
                    <p className="text-sm text-[#7a8077]">
                        {t('No images to export for these samples.')}
                    </p>
                ) : (
                    <div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="from">{t('From')}</Label>
                                <Input
                                    id="from"
                                    type="datetime-local"
                                    value={fromLocal}
                                    min={toLocalInput(bounds.min)}
                                    max={toLocalInput(bounds.max)}
                                    onChange={(e) =>
                                        setFromLocal(e.target.value)
                                    }
                                    className="mt-1.5"
                                />
                            </div>
                            <div>
                                <Label htmlFor="to">{t('To')}</Label>
                                <Input
                                    id="to"
                                    type="datetime-local"
                                    value={toLocal}
                                    min={toLocalInput(bounds.min)}
                                    max={toLocalInput(bounds.max)}
                                    onChange={(e) => setToLocal(e.target.value)}
                                    className="mt-1.5"
                                />
                            </div>
                        </div>
                        <p className="mt-2 text-xs text-[#7a8077]">
                            <span className="font-medium text-[#1c2620] tabular-nums">
                                {selectedCount}
                            </span>{' '}
                            {t('image(s) selected')}
                        </p>
                    </div>
                )}
            </div>

            <div className="mt-6 border-t border-[#ecede8] pt-5">
                {(error || notice) && (
                    <div
                        className={`mb-5 rounded-md border px-4 py-2.5 text-sm ${
                            error
                                ? 'border-red-200 bg-red-50 text-red-700'
                                : 'border-[#bfe3cf] bg-[#eef8f1] text-[#245741]'
                        }`}
                    >
                        {error ?? notice}
                    </div>
                )}
                <div className="divide-y divide-[#ecede8]">
                    <FormatRow
                        title={t('Full JP2 files')}
                        hint={t('One .zip, with a folder per sample.')}
                    >
                        <Button
                            onClick={() => handleExport('zip')}
                            disabled={!ready}
                            className="w-44 bg-[#2d6a4f] hover:bg-[#245741] text-white"
                        >
                            {isPending && pendingType === 'zip' ? (
                                <Loader2Icon className="h-4 w-4 mr-1.5 animate-spin" />
                            ) : (
                                <FileArchiveIcon className="h-4 w-4 mr-1.5" />
                            )}
                            {t('Download .zip')}
                        </Button>
                    </FormatRow>
                    <FormatRow
                        title={t('Timelapse (.mp4)')}
                        hint={t('One video per sample.')}
                    >
                        <Select value={period} onValueChange={setPeriod}>
                            <SelectTrigger className="w-36">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PERIODS.map((p) => (
                                    <SelectItem key={p.value} value={p.value}>
                                        {p.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            onClick={() => handleExport('timelapse')}
                            disabled={!ready}
                            className="w-44 bg-[#2d6a4f] hover:bg-[#245741] text-white"
                        >
                            {isPending && pendingType === 'timelapse' ? (
                                <Loader2Icon className="h-4 w-4 mr-1.5 animate-spin" />
                            ) : (
                                <FilmIcon className="h-4 w-4 mr-1.5" />
                            )}
                            {samplesWithImages.length > 1
                                ? t('Generate {{count}} .mp4', {
                                      count: samplesWithImages.length,
                                  })
                                : t('Generate .mp4')}
                        </Button>
                    </FormatRow>
                </div>
            </div>
        </Section>
    );
}

/** A format: what it gives on the left, its controls on the right. */
function FormatRow({
    title,
    hint,
    children,
}: {
    title: string;
    hint: string;
    children: ReactNode;
}) {
    return (
        <div className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
            <div>
                <p className="text-sm font-medium text-[#1c2620]">{title}</p>
                <p className="mt-0.5 text-xs text-[#7a8077]">{hint}</p>
            </div>
            <div className="flex flex-wrap gap-2">{children}</div>
        </div>
    );
}
